// src/components/services/AILegalResearcher/AILegalResearcher.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Search, BookOpen, AlertCircle, Sparkles } from 'lucide-react';
import { createApi } from '../../../services/api';
import { queryTypes } from './constants';
import { useAuth } from '../../../contexts/AuthContext';
import MpesaPayment from '../../MpesaPayment/MpesaPayment';

export const AILegalResearcher = () => {
  const navigate = useNavigate();
  const { token, signIn } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [question, setQuestion] = useState('');
  const [selectedType, setSelectedType] = useState('case_summaries');
  const [showExamples, setShowExamples] = useState(false);
  const [balance, setBalance] = useState(null);
  const [minRequired, setMinRequired] = useState(0);
  const [requiredCredits, setRequiredCredits] = useState(0);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const api = createApi(token);
  const selectedTypeInfo = queryTypes.find(type => type.value === selectedType);

  // Debug logging
  useEffect(() => {
    console.log("Component mounted, showPaymentForm:", showPaymentForm);
  }, []);

  useEffect(() => {
    if (token) {
      fetchBalance();
    }
  }, [token]);

  const fetchBalance = async () => {
    try {
      const response = await api.getBalance();
      setBalance(response.balance);
      setMinRequired(response.min_required);
      setRequiredCredits(response.required_credits);

      if (response.balance < response.min_required) {
        setShowPaymentForm(true);
      }
    } catch (error) {
      console.error('Error fetching balance:', error);
      setError('Failed to fetch credit balance');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      try {
        await signIn();
        const balanceResponse = await api.getBalance();
        if (balanceResponse.balance >= balanceResponse.min_required) {
          submitQuestion();
        } else {
          setRequiredCredits(balanceResponse.required_credits);
          setShowPaymentForm(true);
          setIsLoading(false);
        }
      } catch (error) {
        setError('Please sign in to use this feature');
        return;
      }
    } else {
      submitQuestion();
    }
  };

  const submitQuestion = async () => {
    if (!question.trim()) {
      setError('Please enter a question');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      // First check if we have enough credits
      const balanceResponse = await api.getBalance();
      if (balance < minRequired) {
        setRequiredCredits(minRequired - balance);
        setShowPaymentForm(true);
        setIsLoading(false);
        return;
      }

      const response = await api.submitQuestion({
        question: question.trim(),
        selectedType
      });

      if (response.api_request_id) {
        await fetchBalance();
        navigate('/history');
      } else {
        throw new Error('No request ID received');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExampleClick = (example) => {
    setQuestion(example);
  };

  const handlePaymentComplete = async () => {
    console.log("Payment completed, closing modal");
    setShowPaymentForm(false);
    await fetchBalance();
  };

  const openPaymentModal = () => {
    console.log("Opening payment modal");
    setShowPaymentForm(true);
  };

  return (
    <div className="mx-auto space-y-8 max-w-4xl">
      {/* Introduction Card */}
      <div className="relative overflow-hidden bg-gradient-to-r from-indigo-600 to-purple-600 p-8 rounded-2xl shadow-lg text-white">
        <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
        <div className="absolute top-10 right-10 w-20 h-20 bg-purple-500/30 rounded-full blur-xl"></div>
        
        <div className="flex items-start space-x-5 relative z-10">
          <div className="bg-white/20 rounded-xl p-3 backdrop-blur-sm">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold mb-3">AI Legal Researcher</h3>
            <p className="text-indigo-100 leading-relaxed">
              Your intelligent assistant for Kenyan legal research. This tool helps you quickly find relevant cases,
              legal principles, and precedents from Kenya's case law database. Select a search type and enter your
              query to get started.
            </p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Credit Balance */}
      {balance !== null && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-indigo-100">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-sm font-medium text-gray-600 block mb-1">Available Credits</span>
              <span className="text-3xl font-bold text-indigo-700">{balance}</span>
            </div>
            {minRequired > 0 && (
              <div className="text-right">
                <span className="text-sm text-gray-500 block mb-1">
                  Required per query: <span className="font-medium text-gray-700">{minRequired}</span>
                </span>
                {requiredCredits > 0 && (
                  <button
                    onClick={openPaymentModal}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    Top Up Credits {requiredCredits > 0 ? `(${requiredCredits} needed)` : ''}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Form */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Query Type Selection */}
            <div className="space-y-4">
              <label className="block text-lg font-semibold text-gray-900">
                Research Type
              </label>
              <div className="relative">
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 pl-4 pr-10 py-3 bg-white shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-shadow text-gray-800 appearance-none"
                >
                  {queryTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                <p className="text-sm text-indigo-800">
                  {selectedTypeInfo.description}
                </p>
              </div>
            </div>

            {/* Question Input */}
            <div className="space-y-4">
              <label className="block text-lg font-semibold text-gray-900">
                Research Question
              </label>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="w-full rounded-lg border border-gray-300 shadow-sm p-4 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-shadow min-h-[120px] text-gray-800"
                placeholder="Enter your legal research question..."
                required
              />

              {/* Example Queries */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <div className="flex items-center space-x-2">
                    <BookOpen className="h-4 w-4 text-indigo-600" />
                    <span className="font-medium text-gray-800">Example Queries</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowExamples(!showExamples)}
                    className="text-indigo-600 hover:text-indigo-800 flex items-center space-x-1 text-sm font-medium transition-colors"
                  >
                    <span>{showExamples ? 'Hide' : 'Show'} Examples</span>
                    {showExamples ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
                
                {showExamples && (
                  <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
                    {selectedTypeInfo.examples.map((example, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleExampleClick(example)}
                        className="w-full text-left p-3 rounded-lg hover:bg-white text-gray-700 hover:text-indigo-600 hover:shadow-sm transition-all border border-transparent hover:border-indigo-100 flex items-center space-x-2"
                      >
                        <Search className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm">{example}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !question.trim()}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white py-4 rounded-lg font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                'Start Research'
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]"
          onClick={() => setShowPaymentForm(false)} // Close when clicking outside
        >
          <div 
            className="bg-white rounded-xl w-full max-w-md animate-fade-in-up shadow-2xl"
            onClick={(e) => e.stopPropagation()} // Prevent clicks inside from closing the modal
          >
            <MpesaPayment
              minAmount={100}
              onClose={() => setShowPaymentForm(false)}
              onPaymentComplete={handlePaymentComplete}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AILegalResearcher;